/**
 * COMMS's feed filter control: the filter icon at the right of the COMMS / OMNINET FEED header and
 * the menu it opens (`comms-redesign` Phase 1, designed with @ben 2026-10-04). Four checkboxes, one
 * per {@link FeedFilterKind}, each saying what it shows, and RESET back to the defaults. Away from
 * the defaults the icon carries a dot and the header says how many types are hidden.
 *
 * The selection lives in {@link CommsFilterProvider}, which the shell wraps around everything: the
 * header holds the menu, but the feed it filters is whichever `CommsApp` holds the column (the
 * shell's own, or COMBAT's portalled one), and a scene change swaps those without resetting the
 * choice. The provider holds the state itself so a toggle re-renders only its consumers, never the
 * stage. Outside the shell there is no provider and {@link useFeedFilter} falls back to local state.
 *
 * Esc and an outside press close the menu. Both hand focus back to the icon when it was inside the
 * menu; an outside press on another control still focuses that control, since the browser's own
 * focus change follows the `pointerdown`.
 */
import { createContext, useContext, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { FEED_FILTER_KINDS, FEED_FILTER_OPTIONS, defaultFeedFilter, feedFilterBadge, isDefaultFeedFilter, toggleFeedFilter, type FeedFilterKind } from "./commsFilter";
import "./comms-app.css";

export type FeedFilterState = {
  selected: ReadonlySet<FeedFilterKind>;
  setSelected: (next: ReadonlySet<FeedFilterKind>) => void;
};

const CommsFilterContext = createContext<FeedFilterState | undefined>(undefined);

export function CommsFilterProvider({ children }: { children: ReactNode }) {
  const [selected, setSelected] = useState<ReadonlySet<FeedFilterKind>>(defaultFeedFilter);
  const value = useMemo(() => ({ selected, setSelected }), [selected]);
  return <CommsFilterContext.Provider value={value}>{children}</CommsFilterContext.Provider>;
}

/** The shared selection under {@link CommsFilterProvider}, else this component's own. */
export function useFeedFilter(): FeedFilterState {
  const shared = useContext(CommsFilterContext);
  const [selected, setSelected] = useState<ReadonlySet<FeedFilterKind>>(defaultFeedFilter);
  return shared ?? { selected, setSelected };
}

export function CommsFilterMenu() {
  const { selected, setSelected } = useFeedFilter();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const ids = useId();
  const atDefaults = isDefaultFeedFilter(selected);
  const badge = feedFilterBadge(selected);

  const items = () => Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)') ?? []);

  useEffect(() => {
    if (!open) return;
    items()[0]?.focus();
    const close = () => {
      if (menuRef.current?.contains(document.activeElement)) buttonRef.current?.focus();
      setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      // One press, one dismissal: COMBAT's board Esc (a window listener) never sees it.
      e.stopPropagation();
      close();
    };
    const onPointer = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!menuRef.current?.contains(t) && !buttonRef.current?.contains(t)) close();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  // Arrow keys rove between the menu items; Tab leaves the menu and closes it.
  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Tab") {
      setOpen(false);
      return;
    }
    const list = items();
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      e.key === "ArrowDown" ? (at + 1) % list.length : e.key === "ArrowUp" ? (at - 1 + list.length) % list.length : e.key === "Home" ? 0 : e.key === "End" ? list.length - 1 : -1;
    if (next < 0) return;
    e.preventDefault();
    list[next]?.focus();
  };

  const reset = () => {
    setSelected(defaultFeedFilter());
    // RESET disables itself; keep focus in the menu.
    items()[0]?.focus();
  };

  return (
    <div className="comms-filter">
      {badge !== null && (
        <span className="comms-filter-badge" aria-hidden="true">
          {badge}
        </span>
      )}
      <button
        ref={buttonRef}
        type="button"
        className={`comms-filter-toggle${open ? " open" : ""}`}
        aria-label={badge === null ? "Filter the feed" : `Filter the feed, ${badge.toLowerCase()}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? `${ids}menu` : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
          <path d="M2 3h16l-6 7.5V17l-4-2v-4.5z" />
        </svg>
        {!atDefaults && <span className="comms-filter-dot" />}
      </button>
      {open && (
        <div
          ref={menuRef}
          id={`${ids}menu`}
          className="comms-filter-menu"
          role="menu"
          aria-labelledby={`${ids}title`}
          aria-describedby={`${ids}sub ${ids}note`}
          onKeyDown={onMenuKey}
        >
          <div className="comms-filter-head" role="none">
            <span id={`${ids}title`} className="comms-filter-title">
              SHOW IN THE FEED
            </span>
            <span id={`${ids}sub`} className="comms-filter-sub">
              Only changes your screen.
            </span>
          </div>
          {FEED_FILTER_KINDS.map((kind) => {
            const on = selected.has(kind);
            return (
              <button
                key={kind}
                type="button"
                role="menuitemcheckbox"
                aria-checked={on}
                aria-labelledby={`${ids}${kind}`}
                aria-describedby={`${ids}${kind}-desc`}
                data-kind={kind}
                className="comms-filter-opt"
                onClick={() => setSelected(toggleFeedFilter(selected, kind))}
              >
                <span className={`comms-filter-box${on ? " on" : ""}`} aria-hidden="true">
                  {on && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M2 6l3 3 5-6" />
                    </svg>
                  )}
                </span>
                <span className="comms-filter-text">
                  <span id={`${ids}${kind}`} className="comms-filter-label">
                    {FEED_FILTER_OPTIONS[kind].label}
                  </span>
                  <span id={`${ids}${kind}-desc`} className="comms-filter-desc">{FEED_FILTER_OPTIONS[kind].description}</span>
                </span>
              </button>
            );
          })}
          <div className="comms-filter-foot" role="none">
            <span id={`${ids}note`} className="comms-filter-note">
              Errors meant for you always show.
            </span>
            <button type="button" role="menuitem" className="comms-filter-reset" disabled={atDefaults} onClick={reset}>
              RESET
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
